import { createAsyncThunk, createSelector, createSlice, } from '@reduxjs/toolkit';
import * as offlineLicensesService from '../services/offlineLicenses';
import { userConfigsSelectors } from './userConfigs';
import { entitiesAdapter } from './common/adapter';

const initialState = {
  isFetching: false,
  isRemoving: false,
  isRestoring: false,
  isCreating: false,
  isUpdating: false,

  creatingUpdatingError: null,
  fetchingError: null,
  removingRestoringError: null,
  downloadingError: null,
};

const sliceName = 'offlineLicenses';

export const fetchAll = createAsyncThunk(
  `${sliceName}/fetchAll`,
  (args, thunkAPI) => {
    return offlineLicensesService.getOfflineLicenses()
      .catch((error) => {
        error.clientMessage = "Can't get offlineLicenses from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const fetchById = createAsyncThunk(
  `${sliceName}/fetchById`,
  (args, thunkAPI) => {
    return offlineLicensesService.getOfflineLicenseById(args.id)
      .catch((error) => {
        error.clientMessage = "Can't get offlineLicense from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const create = createAsyncThunk(
  `${sliceName}/create`,
  (args, thunkAPI) => {
    return offlineLicensesService.createNewOfflineLicense(
      args.clientId, args.productName, args.licenseKeyType, args.deviceKey,
      args.activationDate, args.supportDayCount, args.connectionCount,
      args.webConnectionCount, args.webValidityTerm, args.dicomConnectionLimitationType
    )
      .catch((error) => {
        error.clientMessage = "Can't post offlineLicense to server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const update = createAsyncThunk(
  `${sliceName}/update`,
  (args, thunkAPI) => {
    return offlineLicensesService.updateOfflineLicense(
      args.licenseId, args.supportDayCount, args.webConnectionCount,
      args.connectionCount, args.webValidityTerm, args.dicomConnectionLimitationType, args.deviceKey
    )
      .catch((error) => {
        error.clientMessage = "Can't update offlineLicenses on server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const download = createAsyncThunk(
  `${sliceName}/download`,
  (args, thunkAPI) => {
    return offlineLicensesService
      .downloadLicense(args.licenseId)
      .catch((error) => {
        error.clientMessage = "Can't update offlineLicenses on server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const restore = createAsyncThunk(
  `${sliceName}/restore`,
  (args, thunkAPI) => {
    return offlineLicensesService
      .restoreOfflineLicenses(args.licenseIds)
      .catch((error) => {
        error.clientMessage = "Can't update offlineLicenses on server";
        return thunkAPI.rejectWithValue(error)
      });
  }
)

export const remove = createAsyncThunk(
  `${sliceName}/remove`,
  (args, thunkAPI) => {
    return offlineLicensesService
      .removeOfflineLicenses(args.licenseIds)
      .catch((error) => {
        error.clientMessage = "Can't update offlineLicenses on server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const addToFavorites = createAsyncThunk(
  `${sliceName}/favoritesAdd`,
  (args) => {
    return offlineLicensesService.addOfflineLicenseToFavorites(args.id)
  }
);
export const removeFromFavorites = createAsyncThunk(
  `${sliceName}/favoritesRemove`,
  (args) => {
    return offlineLicensesService.removeOfflineLicenseFromFavorites(args.id)
  }
);

const offlineLicenses = createSlice({
  name: sliceName,
  initialState: entitiesAdapter.getInitialState(initialState),
  reducers: {},
  extraReducers: {
    [fetchAll.fulfilled]: (state, action) => {
      state.isFetching = false;
      entitiesAdapter.setAll(state, action);
    },
    [fetchById.fulfilled]: (state, action) => {
      state.isFetching = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [create.fulfilled]: (state, action) => {
      state.isCreating = false;
      entitiesAdapter.addOne(state, action);
    },
    [update.fulfilled]: (state, action) => {
      state.isUpdating = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [remove.fulfilled]: (state, action) => {
      state.isRemoving = false;
      entitiesAdapter.upsertMany(state, action);
    },
    [restore.fulfilled]: (state, action) => {
      state.isRestoring = false;
      entitiesAdapter.upsertMany(state, action);
    },
    [addToFavorites.fulfilled]: entitiesAdapter.upsertOne,
    [removeFromFavorites.fulfilled]: entitiesAdapter.upsertOne,

    [fetchAll.pending]: (state) => {
      state.isFetching = true;
      state.fetchingError = null;
    },
    [fetchById.pending]: (state) => {
      state.isFetching = true;
      state.fetchingError = null;
    },
    [create.pending]: (state) => {
      state.isCreating = true;
      state.creatingUpdatingError = null;
    },
    [update.pending]: (state) => {
      state.isUpdating = true;
      state.creatingUpdatingError = null;
    },
    [remove.pending]: (state) => {
      state.isRemoving = true;
      state.removingRestoringError = null;
    },
    [restore.pending]: (state) => {
      state.isRestoring = true;
      state.removingRestoringError = null;
    },

    [fetchAll.rejected]: (state, action) => {
      state.isFetching = false;
      state.fetchingError = action.payload;
    },
    [fetchById.rejected]: (state, action) => {
      state.isFetching = false;
      state.fetchingError = action.payload;
    },
    [create.rejected]: (state, action) => {
      state.isCreating = false;
      console.log(action.payload)
      state.creatingUpdatingError = action.payload;
    },
    [update.rejected]: (state, action) => {
      state.isUpdating = false;
      state.creatingUpdatingError = action.payload;
    },
    [remove.rejected]: (state, action) => {
      state.isRemoving = false;
      state.removingRestoringError = action.payload;
    },
    [restore.rejected]: (state, action) => {
      state.isRestoring = false;
      state.removingRestoringError = action.payload;
    },
    [download.rejected]: (state, action) => {
      state.downloadingError = action.payload;
    }
  },
});

const { reducer } = offlineLicenses;

const offlineLicensesAdapterSelectors = entitiesAdapter.getSelectors((state) => state[sliceName]);

const selectedLicensesSelector = createSelector(
  (state) => userConfigsSelectors.selectedItems(state, sliceName),
  (state) => offlineLicensesAdapterSelectors.selectEntities(state),
  (selectedIds, entities) => selectedIds.map((id) => entities[id]),
)

export const offlineLicensesSelectors = {
  ...offlineLicensesAdapterSelectors,
  isFetching: (state) => state[sliceName].isFetching,
  isCreating: (state) => state[sliceName].isCreating,
  isUpdating: (state) => state[sliceName].isUpdating,
  isRemoving: (state) => state[sliceName].isRemoving,
  isRestoring: (state) => state[sliceName].isRestoring,

  fetchingError: (state) => state[sliceName].fetchingError,
  creatingUpdatingError: (state) => state[sliceName].creatingUpdatingError,
  removingRestoringError: (state) => state[sliceName].removingRestoringError,
  downloadingError: (state) => state[sliceName].downloadingError,

  state: (state) => state[sliceName],
  selectedLicenses: selectedLicensesSelector,
};

export default reducer;



// WEBPACK FOOTER //
// ./src/store/offlineLicensesSlice.js